'use client';
import { useState, useId, useRef, useEffect } from 'react';
import { useRouter, useParams, useSearchParams } from 'next/navigation';
import Button from '@/components/shared/Buttons/BaseButton';
import { useGreenhouse } from '@/contexts/GreenhouseProvider';
import { ChevronDown, ChevronUp } from '@1debit/icons';

function toSafeId(value: string): string {
  return value.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');
}

const CareerSearchFilters = () => {
  const { jobLocations, jobTeams } = useGreenhouse();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [keyword, setKeyword] = useState(searchParams.get('keyword') ?? '');
  const [selectedLocations, setSelectedLocations] = useState<string[]>(searchParams.getAll('location'));
  const [selectedTeams, setSelectedTeams] = useState<string[]>(searchParams.getAll('team'));
  const [isRemote, setIsRemote] = useState(searchParams.get('remote') === 'true');

  const locationsRef = useRef<HTMLDivElement>(null);
  const teamsRef = useRef<HTMLDivElement>(null);
  const locationsButtonRef = useRef<HTMLButtonElement>(null);
  const teamsButtonRef = useRef<HTMLButtonElement>(null);

  const locationsHeadingId = useId();
  const locationsPanelId = useId();
  const teamsHeadingId = useId();
  const teamsPanelId = useId();
  const [locationsExpanded, setLocationsExpanded] = useState(false);
  const [teamsExpanded, setTeamsExpanded] = useState(false);

  // updates filters
  const [prevParams, setPrevParams] = useState(searchParams.toString());
  if (searchParams.toString() !== prevParams) {
    setPrevParams(searchParams.toString());
    setKeyword(searchParams.get('keyword') ?? '');
    setSelectedLocations(searchParams.getAll('location'));
    setSelectedTeams(searchParams.getAll('team'));
    setIsRemote(searchParams.get('remote') === 'true');
  }

  // Closes accordions when user clicks outside; return focus to trigger when closing
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (locationsExpanded && locationsRef.current && !locationsRef.current.contains(target)) {
        setLocationsExpanded(false);
      }
      if (teamsExpanded && teamsRef.current && !teamsRef.current.contains(target)) {
        setTeamsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClick, true);
    return () => document.removeEventListener('mousedown', handleClick, true);
  }, [locationsExpanded, teamsExpanded]);

  // Closes accordions when user tabs away
  const handleAccordionBlur = (e: React.FocusEvent<HTMLDivElement>, setter: (val: boolean) => void) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) {
      setter(false);
    }
  };

  // Closes accordions when user uses ESC key
  const handleAccordionEsc = (
    e: React.KeyboardEvent<HTMLDivElement>,
    setter: (val: boolean) => void,
    buttonRef: React.RefObject<HTMLButtonElement | null>,
  ) => {
    if (e.key === 'Escape') {
      setter(false);
      buttonRef.current?.focus();
    }
  };

  const toggleCheckbox = (list: string[], item: string) =>
    list.includes(item) ? list.filter(i => i !== item) : [...list, item];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams();

    if (keyword) newParams.set('keyword', keyword);
    selectedLocations.forEach(loc => newParams.append('location', loc));
    selectedTeams.forEach(team => newParams.append('team', team));
    if (isRemote) newParams.set('remote', 'true');

    newParams.set('page', '1');
    router.push(`/careers/jobs/?${newParams.toString()}`);
  };

  const handleReset = () => {
    setKeyword('');
    setSelectedLocations([]);
    setSelectedTeams([]);
    setIsRemote(false);
    setLocationsExpanded(false);
    setTeamsExpanded(false);
    router.push(`/careers/jobs/`);
    setPrevParams(searchParams.toString());
  };

  const hasFilters = Boolean(keyword.trim() || selectedLocations.length > 0 || selectedTeams.length > 0 || isRemote);

  const locationsEmpty = jobLocations.length === 0;
  const teamsEmpty = jobTeams.length === 0;

  return (
    <form
      data-testid="form"
      className="relative z-1 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-y-4"
      onSubmit={handleSubmit}>
      <div className="w-full">
        <label className="sr-only" htmlFor="jobs">
          Search Jobs
        </label>
        <input
          id="jobs"
          name="keyword"
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder="Search Jobs"
          className="block w-full white-colorway font-semibold rounded-md hover:cursor-pointer placeholder:text-(--kale) border-pebble"
        />
      </div>

      <div className="sm:w-full flex flex-col sm:flex-row sm:flex-wrap sm:justify-between gap-3">
        <div
          ref={locationsRef}
          className="relative sm:flex-1 w-full"
          onKeyDown={e => handleAccordionEsc(e, setLocationsExpanded, locationsButtonRef)}
          onBlur={e => handleAccordionBlur(e, setLocationsExpanded)}>
          <button
            ref={locationsButtonRef}
            type="button"
            id={locationsHeadingId}
            aria-expanded={locationsExpanded}
            aria-controls={locationsPanelId}
            aria-disabled={locationsEmpty}
            disabled={locationsEmpty}
            onClick={() => setLocationsExpanded(prev => !prev)}
            className={`w-full flex justify-between items-center font-semibold text-left px-3 py-2 white-colorway border border-pebble hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              locationsExpanded ? 'rounded-t-md' : 'rounded-md'
            }`}>
            <span className="block truncate">
              {selectedLocations.length > 0
                ? selectedLocations.map(loc => loc.split(',')[0].trim()).join(', ')
                : 'All Locations'}
            </span>
            {locationsExpanded ? <ChevronUp /> : <ChevronDown />}
          </button>
          <div
            id={locationsPanelId}
            role="region"
            aria-labelledby={locationsHeadingId}
            aria-hidden={!locationsExpanded}
            hidden={!locationsExpanded}
            onMouseDown={e => e.stopPropagation()}
            className="absolute z-10 top-full w-full flex flex-col gap-2 px-3 py-2 white-colorway rounded-b-md border border-t-0 border-pebble max-h-60 overflow-y-auto">
            {jobLocations.map((location, index) => (
              <label
                className="font-semibold cursor-pointer flex items-center"
                key={location}
                htmlFor={`loc-accordion-${toSafeId(location)}-${index}`}>
                <input
                  className="w-[20px] h-[20px] mr-2 rounded text-(--kale) border-(--kale) focus:(--kale)"
                  type="checkbox"
                  id={`loc-accordion-${toSafeId(location)}-${index}`}
                  name="location"
                  value={location}
                  checked={selectedLocations.includes(location)}
                  onChange={() => setSelectedLocations(prev => toggleCheckbox(prev, location))}
                />
                {location}
              </label>
            ))}
          </div>
        </div>

        <div
          ref={teamsRef}
          onKeyDown={e => handleAccordionEsc(e, setTeamsExpanded, teamsButtonRef)}
          onBlur={e => handleAccordionBlur(e, setTeamsExpanded)}
          className="relative sm:flex-1 w-full">
          <button
            ref={teamsButtonRef}
            type="button"
            id={teamsHeadingId}
            aria-expanded={teamsExpanded}
            aria-controls={teamsPanelId}
            aria-disabled={teamsEmpty}
            disabled={teamsEmpty}
            onClick={() => setTeamsExpanded(prev => !prev)}
            className={`w-full flex justify-between items-center font-semibold text-left px-3 py-2 white-colorway border border-pebble hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
              teamsExpanded ? 'rounded-t-md' : 'rounded-md'
            }`}>
            <span className="block truncate">{selectedTeams.length > 0 ? selectedTeams.join(', ') : 'All Teams'}</span>
            {teamsExpanded ? <ChevronUp /> : <ChevronDown />}
          </button>
          <div
            id={teamsPanelId}
            role="region"
            aria-labelledby={teamsHeadingId}
            aria-hidden={!teamsExpanded}
            hidden={!teamsExpanded}
            onMouseDown={e => e.stopPropagation()}
            className="absolute z-10 top-full w-full flex flex-col gap-2 px-3 py-2 white-colorway rounded-b-md border border-t-0 border-pebble max-h-60 overflow-y-auto">
            {jobTeams.map((team, index) => (
              <label
                className="font-semibold cursor-pointer flex items-center"
                key={team}
                htmlFor={`teams-accordion-${toSafeId(team)}-${index}`}>
                <input
                  className="w-[20px] h-[20px] mr-2 rounded text-(--kale) border-(--kale) focus:(--kale)"
                  type="checkbox"
                  id={`teams-accordion-${toSafeId(team)}-${index}`}
                  name="team"
                  value={team}
                  checked={selectedTeams.includes(team)}
                  onChange={() => setSelectedTeams(prev => toggleCheckbox(prev, team))}
                />
                {team}
              </label>
            ))}
          </div>
        </div>

        <div className="sm:w-full md:w-fit flex items-center gap-2">
          <input
            id="remote"
            name="remote"
            type="checkbox"
            className="w-[20px] h-[20px] rounded text-(--kale) border-2 border-(--kale) focus:(--kale)"
            checked={isRemote}
            onChange={e => setIsRemote(e.target.checked)}
          />
          <label className="font-bold cursor-pointer" htmlFor="remote">
            Remote Work
          </label>
        </div>
      </div>

      <div className="flex gap-4 items-center mt-2">
        <Button type="submit" className="size-fit">
          Submit
        </Button>
        {hasFilters && (
          <button
            type="button"
            onClick={handleReset}
            className="text-sm font-bold underline hover:no-underline hover:cursor-pointer text-(--kale)">
            Clear Filters
          </button>
        )}
      </div>
    </form>
  );
};

export default CareerSearchFilters;
